import { useEffect, useState, type ImgHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../../ui';
import { sectorPages } from './data';

// Sector tiles for /industries: editorial photo with one UI moment overlaid on the calm side.

function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('rounded-2xl bg-white text-[12.5px] text-bm-ink shadow-[0_24px_50px_-24px_rgba(24,19,67,.45),0_0_0_1px_rgba(36,30,97,.06)]', className)}>{children}</div>;
}

// Deterministic faux QR (decorative)
export function Qr({ size = 74, dark = '#241E61' }: { size?: number; dark?: string }) {
  const n = 21;
  const cells: [number, number][] = [];
  let s = 7;
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      const finder = (x < 7 && y < 7) || (x > 13 && y < 7) || (x < 7 && y > 13);
      if (finder) continue;
      s = (s * 9301 + 49297) % 233280;
      if (s / 233280 > 0.52) cells.push([x, y]);
    }
  const Finder = ({ x, y }: { x: number; y: number }) => (
    <g>
      <rect x={x + 0.5} y={y + 0.5} width="6" height="6" rx="1.4" fill="none" stroke={dark} strokeWidth="1" />
      <rect x={x + 2} y={y + 2} width="3" height="3" rx=".6" fill={dark} />
    </g>
  );
  return (
    <svg width={size} height={size} viewBox={`0 0 ${n} ${n}`} aria-hidden="true" className="shrink-0">
      {cells.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x + 0.1} y={y + 0.1} width=".8" height=".8" rx=".2" fill={dark} />
      ))}
      <Finder x={0} y={0} />
      <Finder x={14} y={0} />
      <Finder x={0} y={14} />
    </svg>
  );
}

/** Tries the photo, then its fallback, then renders nothing (generated shots can land after the page ships). */
export function FallbackImg({ src, fallback, onGone, ...rest }: { src: string; fallback?: string; onGone?: () => void } & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'>) {
  const [cur, setCur] = useState<string | null>(src);
  useEffect(() => setCur(src), [src]);
  if (!cur) return null;
  return (
    <img
      {...rest}
      src={cur}
      onError={() => {
        if (cur === src && fallback) setCur(fallback);
        else {
          setCur(null);
          onGone?.();
        }
      }}
    />
  );
}

/** Photo tile with a single UI chip overlaid on the calm side. */
function PhotoVignette({ src, fallback, alt, chip, sub }: { src: string; fallback?: string; alt: string; chip: string; sub: string }) {
  return (
    <div className="relative h-full w-full overflow-hidden bg-bm-alt">
      <FallbackImg src={src} fallback={fallback} alt={alt} width={1536} height={1024} loading="lazy" className="h-full w-full object-cover" />
      <div className="absolute bottom-5 left-5 right-5 sm:right-auto">
        <Card className="flex items-center gap-3 px-3.5 py-2.5">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#E3F4E9]">
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="m2.5 6.2 2.3 2.3L9.5 3.8" stroke="#2F9E58" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span>
            <span className="block text-[12.5px] font-semibold text-bm-indigo">{chip}</span>
            <span className="block text-[11px] text-bm-muted">{sub}</span>
          </span>
        </Card>
      </div>
    </div>
  );
}

export function SectorVignette({ slug }: { slug: string }) {
  const p = sectorPages[slug];
  if (!p) return null;
  return <PhotoVignette src={p.photos[0].src} fallback={p.photos[0].fallback} alt={p.photos[0].alt} chip={p.chip.t} sub={p.chip.s} />;
}
